import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { RotateCcw, Loader2, FileText, Package, Users, Ship, Layers, Trash2, Eraser } from "lucide-react";
import AppLayout from "@/components/AppLayout";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso || "—"; } };
const keyOf = (it) => `${it.kind}:${it.id}`;

const KIND_META = {
  rfq: { icon: FileText, tint: "text-[#1B4383] bg-[#1B4383]/10" },
  supplier: { icon: Package, tint: "text-emerald-700 bg-emerald-100" },
  client: { icon: Users, tint: "text-amber-700 bg-amber-100" },
  freight_quote: { icon: Ship, tint: "text-sky-700 bg-sky-100" },
  quote_version: { icon: Layers, tint: "text-violet-700 bg-violet-100" },
};

export default function DeletedVersions() {
  const [sections, setSections] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [detail, setDetail] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [selected, setSelected] = useState({}); // key -> item
  // bulkConfirm: { mode: 'section'|'selected', kind?, label, count, items }
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/trash")
      .then((r) => { setSections(r.data.sections || []); setTotal(r.data.total || 0); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const activeSections = useMemo(() => sections.filter((s) => s.count > 0), [sections]);
  const selectedItems = useMemo(() => Object.values(selected), [selected]);

  const restore = async (item) => {
    setBusy(item.id);
    try {
      await api.post(`/admin/trash/${item.kind}/${item.id}/restore`);
      toast.success(`Restored “${item.title}”`);
      setDetail(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const purge = async (item) => {
    setBusy(item.id);
    try {
      await api.delete(`/admin/trash/${item.kind}/${item.id}`);
      toast.success(`Permanently deleted “${item.title}”`);
      setPurgeTarget(null);
      setDetail(null);
      setSelected((s) => { const n = { ...s }; delete n[keyOf(item)]; return n; });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  };

  const toggleSel = (item) => setSelected((s) => {
    const n = { ...s }; const k = keyOf(item);
    if (n[k]) delete n[k]; else n[k] = item;
    return n;
  });

  const toggleSection = (sec, checked) => setSelected((s) => {
    const n = { ...s };
    sec.items.forEach((it) => { const k = keyOf(it); if (checked) n[k] = it; else delete n[k]; });
    return n;
  });

  const runBulk = async () => {
    setBulkBusy(true);
    try {
      if (bulkConfirm.mode === "section") {
        const r = await api.post("/admin/trash/empty", { kind: bulkConfirm.kind });
        toast.success(`Emptied ${bulkConfirm.label} — ${r.data.purged} item(s) removed`);
      } else {
        const items = bulkConfirm.items.map((it) => ({ kind: it.kind, id: it.id }));
        const r = await api.post("/admin/trash/purge-many", { items });
        toast.success(`Permanently deleted ${r.data.purged} item(s)`);
        setSelected({});
      }
      setBulkConfirm(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBulkBusy(false); }
  };

  return (
    <AppLayout title="Recycle Bin">
      <div className="mx-auto max-w-6xl" data-testid="recycle-bin-page">
        <p className="mb-6 text-sm text-muted-foreground">
          Everything deleted across the organisation lands here. Browse by type, open an item to see what was removed, and restore anything by mistake — or clear out old junk for good.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : total === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-white p-10 text-center text-sm text-muted-foreground" data-testid="recycle-empty">
            <Trash2 className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
            Nothing has been deleted. The recycle bin is empty.
          </div>
        ) : (
          <div className="space-y-8 pb-20">
            {activeSections.map((sec) => {
              const Meta = KIND_META[sec.kind] || KIND_META.rfq;
              const Icon = Meta.icon;
              const secSelected = sec.items.filter((it) => selected[keyOf(it)]).length;
              const allChecked = secSelected === sec.items.length && sec.items.length > 0;
              return (
                <section key={sec.kind} data-testid={`trash-section-${sec.kind}`}>
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className={`flex h-7 w-7 items-center justify-center rounded-md ${Meta.tint}`}><Icon className="h-4 w-4" /></span>
                    <h2 className="text-base font-semibold text-[#1B2A4A]">{sec.label}</h2>
                    <Badge variant="secondary" className="ml-1">{sec.count}</Badge>
                    <Button
                      size="sm" variant="ghost"
                      className="ml-auto text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={() => setBulkConfirm({ mode: "section", kind: sec.kind, label: sec.label, count: sec.count })}
                      data-testid={`empty-section-${sec.kind}`}
                    >
                      <Eraser className="mr-1.5 h-3.5 w-3.5" />Empty Section
                    </Button>
                  </div>

                  {/* Desktop table */}
                  <div className="hidden overflow-x-auto rounded-lg border border-border bg-white sm:block">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-left text-xs text-muted-foreground">
                        <tr>
                          <th className="w-10 px-4 py-3">
                            <Checkbox checked={allChecked} onCheckedChange={(v) => toggleSection(sec, !!v)} data-testid={`select-all-${sec.kind}`} aria-label="Select all" />
                          </th>
                          <th className="px-4 py-3 font-medium">Item</th>
                          <th className="px-4 py-3 font-medium">Deleted By</th>
                          <th className="px-4 py-3 font-medium">When</th>
                          <th className="px-4 py-3 font-medium text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sec.items.map((it) => (
                          <tr key={it.id} className="border-t border-border hover:bg-slate-50/60" data-testid={`trash-row-${it.id}`}>
                            <td className="px-4 py-3">
                              <Checkbox checked={!!selected[keyOf(it)]} onCheckedChange={() => toggleSel(it)} data-testid={`select-${it.id}`} aria-label="Select item" />
                            </td>
                            <td className="px-4 py-3">
                              <button
                                className="text-left font-medium text-[#1B4383] hover:underline"
                                onClick={() => setDetail(it)}
                                data-testid={`trash-open-${it.id}`}
                              >
                                {it.title}
                              </button>
                              {it.subtitle ? <div className="text-xs text-muted-foreground">{it.subtitle}</div> : null}
                            </td>
                            <td className="px-4 py-3">{it.deleted_by_name || "—"}</td>
                            <td className="px-4 py-3 text-muted-foreground">{fmt(it.deleted_at)}</td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => restore(it)} disabled={busy === it.id} data-testid={`restore-${it.id}`}>
                                  {busy === it.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1.5 h-3.5 w-3.5" />}Restore
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setPurgeTarget(it)} disabled={busy === it.id} data-testid={`purge-${it.id}`}>
                                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete forever
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="space-y-2 sm:hidden">
                    <label className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
                      <Checkbox checked={allChecked} onCheckedChange={(v) => toggleSection(sec, !!v)} data-testid={`select-all-m-${sec.kind}`} aria-label="Select all" />
                      Select all
                    </label>
                    {sec.items.map((it) => (
                      <div key={it.id} className="rounded-lg border border-border bg-white p-3" data-testid={`trash-card-${it.id}`}>
                        <div className="flex items-start gap-2.5">
                          <Checkbox className="mt-0.5" checked={!!selected[keyOf(it)]} onCheckedChange={() => toggleSel(it)} data-testid={`select-m-${it.id}`} aria-label="Select item" />
                          <button
                            className="min-w-0 flex-1 text-left"
                            onClick={() => setDetail(it)}
                            data-testid={`trash-open-m-${it.id}`}
                          >
                            <div className="break-words font-medium text-[#1B4383]">{it.title}</div>
                            {it.subtitle ? <div className="break-words text-xs text-muted-foreground">{it.subtitle}</div> : null}
                            <div className="mt-1 text-xs text-muted-foreground">{it.deleted_by_name || "—"} · {fmt(it.deleted_at)}</div>
                          </button>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <Button size="sm" variant="outline" className="flex-1" onClick={() => restore(it)} disabled={busy === it.id} data-testid={`restore-m-${it.id}`}>
                            {busy === it.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1.5 h-3.5 w-3.5" />}Restore
                          </Button>
                          <Button size="sm" variant="ghost" className="flex-1 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setPurgeTarget(it)} disabled={busy === it.id} data-testid={`purge-m-${it.id}`}>
                            <Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      {/* Floating action bar for multiselect */}
      {selectedItems.length > 0 && (
        <div className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-4 rounded-full border border-border bg-white px-5 py-3 shadow-xl" data-testid="trash-selection-bar">
          <span className="text-sm font-medium text-[#1B2A4A]">{selectedItems.length} selected</span>
          <Button variant="ghost" size="sm" onClick={() => setSelected({})} data-testid="clear-selection">Clear</Button>
          <Button
            size="sm" className="bg-red-600 hover:bg-red-700"
            onClick={() => setBulkConfirm({ mode: "selected", label: `${selectedItems.length} selected item(s)`, count: selectedItems.length, items: selectedItems })}
            data-testid="delete-selected"
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete selected
          </Button>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="trash-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => { const M = KIND_META[detail.kind] || KIND_META.rfq; const I = M.icon; return <span className={`flex h-7 w-7 items-center justify-center rounded-md ${M.tint}`}><I className="h-4 w-4" /></span>; })()}
                  {detail.title}
                </DialogTitle>
              </DialogHeader>
              {detail.subtitle ? <p className="-mt-1 text-sm text-muted-foreground">{detail.subtitle}</p> : null}
              <dl className="mt-2 divide-y divide-border rounded-lg border border-border">
                {(detail.summary || []).map((row, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`trash-detail-row-${i}`}>
                    <dt className="text-muted-foreground">{row.label}</dt>
                    <dd className="font-medium text-[#1B2A4A]">{row.value}</dd>
                  </div>
                ))}
                <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <dt className="text-muted-foreground">Deleted by</dt>
                  <dd className="font-medium text-[#1B2A4A]">{detail.deleted_by_name || "—"}</dd>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <dt className="text-muted-foreground">Deleted at</dt>
                  <dd className="font-medium text-[#1B2A4A]">{fmt(detail.deleted_at)}</dd>
                </div>
              </dl>
              <DialogFooter>
                <Button variant="ghost" className="mr-auto text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setPurgeTarget(detail)} disabled={busy === detail.id} data-testid="trash-detail-purge">
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete forever
                </Button>
                <Button variant="outline" onClick={() => setDetail(null)} data-testid="trash-detail-close">Close</Button>
                <Button onClick={() => restore(detail)} disabled={busy === detail.id} data-testid="trash-detail-restore">
                  {busy === detail.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1.5 h-3.5 w-3.5" />}Restore
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!purgeTarget} onOpenChange={(o) => !o && setPurgeTarget(null)}>
        <AlertDialogContent data-testid="purge-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete forever?</AlertDialogTitle>
            <AlertDialogDescription>
              “{purgeTarget?.title}” will be permanently removed from the recycle bin. This <strong>cannot be undone</strong> — you will no longer be able to restore it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="purge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => { e.preventDefault(); if (purgeTarget) purge(purgeTarget); }}
              disabled={busy === purgeTarget?.id}
              data-testid="purge-confirm"
            >
              {busy === purgeTarget?.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Trash2 className="mr-1.5 h-3.5 w-3.5" />}Delete forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!bulkConfirm} onOpenChange={(o) => !o && !bulkBusy && setBulkConfirm(null)}>
        <AlertDialogContent data-testid="bulk-purge-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{bulkConfirm?.mode === "section" ? "Empty this section?" : "Delete selected forever?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {bulkConfirm?.mode === "section"
                ? <>All <strong>{bulkConfirm?.count}</strong> item(s) in <strong>{bulkConfirm?.label}</strong> will be permanently removed.</>
                : <><strong>{bulkConfirm?.count}</strong> selected item(s) will be permanently removed.</>}
              {" "}This <strong>cannot be undone</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy} data-testid="bulk-purge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={(e) => { e.preventDefault(); runBulk(); }}
              disabled={bulkBusy}
              data-testid="bulk-purge-confirm"
            >
              {bulkBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Trash2 className="mr-1.5 h-3.5 w-3.5" />}
              {bulkConfirm?.mode === "section" ? "Empty section" : "Delete selected"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
